import React, { useEffect, useState, useRef, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  startOfWeek,
  endOfWeek,
  addDays,
  addWeeks,
  addMonths,
  format,
  parseISO,
  differenceInCalendarDays,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  isSameMonth,
  isToday
} from "date-fns";
import { formatTimeRange, formatShortTime } from "./utils/formatTime";
import { formatWeatherDisplay } from "./utils/weather";
import { SYM, withEllipsis } from "./utils/textSymbols";
import EventModal from "./components/EventModal";
import SmartAddFAB from "./components/SmartAddFAB";
import SmartAddModal from "./components/SmartAddModal";
import ApprovalBell, { APPROVAL_BELL_SLOTS } from "./components/ApprovalBell";
import ApprovalsPanel from "./components/ApprovalsPanel";
import AssistantFab from "./components/AssistantFab";
import AssistantPanel from "./components/AssistantPanel";
import GmailTestingModal from "./components/GmailTestingModal";
import SchoolSettingsModal from "./components/SchoolSettingsModal";
import UpdateBanner from "./components/UpdateBanner";
import "./App.css";
import GroceriesPage from "./pages/GroceriesPage";
import MealsPage from "./pages/MealsPage";
import GamesPage from "./pages/GamesPage";
import TodoPage from "./pages/TodoPage";
import { AUTH_REFRESH_MS, buildLogoutUrl, getPrincipalEmail, parseAuthMePayload, renewSwaSession } from "./utils/swaAuth";

const monthKey = (year, month) => `${year}-${month}`;

const MONTH_WEEKDAY_LABELS = [
  { short: "S", full: "Sun" },
  { short: "M", full: "Mon" },
  { short: "T", full: "Tue" },
  { short: "W", full: "Wed" },
  { short: "T", full: "Thu" },
  { short: "F", full: "Fri" },
  { short: "S", full: "Sat" },
];

const FONT_OPTIONS = [
  { name: "Default", value: "" },
  { name: "Roboto", value: "Roboto" },
  { name: "Open Sans", value: "Open Sans" },
  { name: "Calibri", value: "Calibri" },
  { name: "Aptos", value: "Aptos" },
  { name: "Montserrat", value: "Montserrat" },
  { name: "Playfair Display", value: "Playfair Display" },
  { name: "Merriweather", value: "Merriweather" },
  { name: "Quicksand", value: "Quicksand" },
  { name: "Pacifico", value: "Pacifico" },
  { name: "Dancing Script", value: "Dancing Script" },
  { name: "Great Vibes", value: "Great Vibes" },
  { name: "Lobster", value: "Lobster" },
  { name: "Caveat", value: "Caveat" },
  { name: "Kalam", value: "Kalam" },
  { name: "Cinzel", value: "Cinzel" },
  { name: "Cinzel Decorative", value: "Cinzel Decorative" },
  { name: "Orbitron", value: "Orbitron" },
  { name: "Rajdhani", value: "Rajdhani" },
  { name: "Press Start 2P", value: "Press Start 2P" },
  { name: "Bebas Neue", value: "Bebas Neue" },
  { name: "Permanent Marker", value: "Permanent Marker" },
  { name: "Righteous", value: "Righteous" },
];

const TAB_PATHS = {
  calendars: "/",
  groceries: "/groceries",
  meals: "/meals",
  todo: "/todo",
  games: "/games",
};

const PATH_TABS = {
  "/": "calendars",
  "/calendars": "calendars",
  "/groceries": "groceries",
  "/meals": "meals",
  "/todo": "todo",
  "/games": "games",
};

function tabFromPath(pathname) {
  const clean = (pathname || "/").replace(/\/+$/, "") || "/";
  return PATH_TABS[clean.toLowerCase()] || null;
}

function mapsSearchUrl(location) {
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(location)}`;
}

function EventLocation({ location }) {
  if (!location) return null;
  return (
    <div className="event-location">
      <a
        className="event-location-nav"
        href={mapsSearchUrl(location)}
        target="_blank"
        rel="noopener noreferrer"
        title="Open in Google Maps"
        aria-label={`Open ${location} in Google Maps`}
        draggable={false}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <svg className="event-location-nav-icon" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <path fill="currentColor" d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5a2.5 2.5 0 1 1 0-5 2.5 2.5 0 0 1 0 5z"/>
        </svg>
      </a>
      <span className="event-location-text" title={location}>{location}</span>
    </div>
  );
}

export default function App() {
  const [eventsByMonth, setEventsByMonth] = useState({});
  const [weather, setWeather] = useState({});
  const [weekDates, setWeekDates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [familyName, setFamilyName] = useState("Clark Board");
  const [familyMembers, setFamilyMembers] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [initialFormData, setInitialFormData] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [draggedEventId, setDraggedEventId] = useState(null);
  const [dropTargetDay, setDropTargetDay] = useState(null);
  const [viewMode, setViewMode] = useState("week"); // "week" | "month"
  const [activeTab, setActiveTab] = useState(() => tabFromPath(window.location.pathname) || "calendars"); // "calendars" | "groceries" | "meals" | "todo" | "games"
  const [gamesInGame, setGamesInGame] = useState(false);
  const [approvalsPanelOpen, setApprovalsPanelOpen] = useState(false);
  const [approvalFocusId, setApprovalFocusId] = useState(null);
  const [approvalBellRefresh, setApprovalBellRefresh] = useState(0);
  const [assistantOpen, setAssistantOpen] = useState(false);
  const [pendingApprovalRequest, setPendingApprovalRequest] = useState(null);
  const [viewAnchor, setViewAnchor] = useState(() => {
    const today = new Date();
    return startOfWeek(today, { weekStartsOn: 0 });
  });
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [schoolSettingsOpen, setSchoolSettingsOpen] = useState(false);
  const [gearMenuOpen, setGearMenuOpen] = useState(false);
  const [dailyAgendaDay, setDailyAgendaDay] = useState(null);
  const [weekPickerOpen, setWeekPickerOpen] = useState(false);
  const [monthPickerOpen, setMonthPickerOpen] = useState(false);
  const [pickerMonth, setPickerMonth] = useState(() => new Date());
  const [pickerYear, setPickerYear] = useState(() => new Date().getFullYear());
  const gearMenuRef = useRef(null);
  const viewTitleRef = useRef(null);
  const swipeStartX = useRef(null);
  const swipeStartY = useRef(null);
  const swipeStartedOnEvent = useRef(false);
  const [coziUsername, setCoziUsername] = useState("");
  const [coziPassword, setCoziPassword] = useState("");
  const [coziTestSuccess, setCoziTestSuccess] = useState(false);
  const [coziTestError, setCoziTestError] = useState(null);
  const [coziTestLoading, setCoziTestLoading] = useState(false);
  const [coziSaveSuccess, setCoziSaveSuccess] = useState(false);
  const [coziSaveError, setCoziSaveError] = useState(null);
  const [coziSaveLoading, setCoziSaveLoading] = useState(false);
  const [paprikaEmail, setPaprikaEmail] = useState("");
  const [paprikaPassword, setPaprikaPassword] = useState("");
  const [paprikaTestSuccess, setPaprikaTestSuccess] = useState(false);
  const [paprikaTestError, setPaprikaTestError] = useState(null);
  const [paprikaTestLoading, setPaprikaTestLoading] = useState(false);
  const [paprikaSaveSuccess, setPaprikaSaveSuccess] = useState(false);
  const [paprikaSaveError, setPaprikaSaveError] = useState(null);
  const [paprikaSaveLoading, setPaprikaSaveLoading] = useState(false);
  const [gmailStatus, setGmailStatus] = useState(null);
  const [gmailAllowedInput, setGmailAllowedInput] = useState("");
  const [gmailStatusLoading, setGmailStatusLoading] = useState(false);
  const [gmailStatusError, setGmailStatusError] = useState(null);
  const [gmailSaveLoading, setGmailSaveLoading] = useState(false);
  const [gmailSaveSuccess, setGmailSaveSuccess] = useState(false);
  const [gmailSaveError, setGmailSaveError] = useState(null);
  const [gmailDisconnectLoading, setGmailDisconnectLoading] = useState(false);
  const [gmailTestingOpen, setGmailTestingOpen] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [theme, setTheme] = useState("light");
  const [titleFont, setTitleFont] = useState("");
  const [calendarFont, setCalendarFont] = useState("");
  const [fontPickerTarget, setFontPickerTarget] = useState(null);
  const [backgroundType, setBackgroundType] = useState("color");
  const [backgroundColor, setBackgroundColor] = useState("#2c3e50");
  const [backgroundImage, setBackgroundImage] = useState("");
  const [backgroundImageHistory, setBackgroundImageHistory] = useState([]);
  const [backgroundHistoryLoading, setBackgroundHistoryLoading] = useState(false);
  const [backgroundUploading, setBackgroundUploading] = useState(false);
  const [generateBackgroundOpen, setGenerateBackgroundOpen] = useState(false);
  const [galleryModalOpen, setGalleryModalOpen] = useState(false);
  const [generatePrompt, setGeneratePrompt] = useState("");
  const [generateResultUrl, setGenerateResultUrl] = useState(null);
  const [generateLoading, setGenerateLoading] = useState(false);
  const [generateError, setGenerateError] = useState(null);
  const [aiPromptContext, setAiPromptContext] = useState("");
  const [todoMemberId, setTodoMemberId] = useState("");
  const backgroundFileInputRef = useRef(null);
  const [userEmail, setUserEmail] = useState(null);
  const [smartAddMode, setSmartAddMode] = useState(null);
  const [searchModalOpen, setSearchModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [refreshing, setRefreshing] = useState(false);
  const [refreshFeedback, setRefreshFeedback] = useState(null);
  const searchInputRef = useRef(null);
  const todayColumnRef = useRef(null);
  const hasAutoScrolledToTodayRef = useRef(false);
  const refreshRef = useRef({ getMonthsNeeded: () => [], loadMonths: async () => {} });
  const refreshFeedbackTimerRef = useRef(null);
  const backgroundObjectUrlRef = useRef(null);

  const PREF_CACHE_KEY = "family-dashboard-preferences";
  const EVENTS_CACHE_KEY = "family-dashboard-events";

  // API base: use relative when behind SWA proxy (no env set), else explicit URL for local dev
  const apiBase =
    process.env.REACT_APP_API_URL || "";

  const location = useLocation();
  const navigate = useNavigate();

  const participantNames =
    familyMembers.length > 0
      ? familyMembers.map((m) => m.name).sort((a, b) => a.localeCompare(b))
      : [];

  const refreshApprovalBell = useCallback(() => {
    setApprovalBellRefresh((n) => n + 1);
  }, []);

  const switchTab = useCallback((tab) => {
    setActiveTab(tab);
    refreshApprovalBell();
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    const path = TAB_PATHS[tab];
    if (path && location.pathname !== path) {
      navigate(path);
    }
  }, [refreshApprovalBell, navigate, location.pathname]);

  useEffect(() => {
    refreshApprovalBell();
  }, [refreshApprovalBell]);

  // Keep the active tab in sync with the URL (refresh, deep links, back/forward).
  useEffect(() => {
    const tab = tabFromPath(location.pathname);
    if (tab && tab !== activeTab) {
      setActiveTab(tab);
    }
  }, [location.pathname, activeTab]);

  const loadGmailStatus = useCallback(async () => {
    setGmailStatusLoading(true);
    setGmailStatusError(null);
    try {
      const res = await fetch(`${apiBase}/api/GetGmailIntegrationStatus`, { credentials: "include" });
      const data = await res.json().catch(() => ({}));
      if (res.status === 403) {
        setGmailStatus({ adminOnly: true });
        return;
      }
      if (!res.ok) {
        throw new Error(data.message || data.error || `Error ${res.status}`);
      }
      setGmailStatus(data);
      setGmailAllowedInput((data.allowedSenders || []).join(", "));
    } catch (err) {
      setGmailStatusError(err.message || "Could not load Gmail settings.");
    } finally {
      setGmailStatusLoading(false);
    }
  }, [apiBase]);

  useEffect(() => {
    if (settingsOpen) loadGmailStatus();
  }, [settingsOpen, loadGmailStatus]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const gmail = params.get("gmail");
    if (!gmail) return;
    setSettingsOpen(true);
    if (gmail === "connected") {
      setGmailSaveSuccess(true);
      setGmailStatusError(null);
      loadGmailStatus();
    } else if (gmail === "error") {
      setGmailStatusError(params.get("message") || "Gmail connection failed.");
    }
    navigate(location.pathname, { replace: true });
  }, [location.search, location.pathname, navigate, loadGmailStatus]);

  const openCalendarApproval = useCallback((req, items, options = {}) => {
    const list = items || req.items || [];
    const first = list[0]?.payload || list[0];
    if (!first) return;
    setPendingApprovalRequest({ requestId: req.id, items: list, partial: !!options.partial });
    setApprovalsPanelOpen(false);
    switchTab("calendars");
    setSelectedEvent(null);
    const startDate = first.startDate || first.startDay || format(new Date(), "yyyy-MM-dd");
    setSelectedDate(parseISO(`${startDate}T12:00:00`));
    setInitialFormData({
      title: first.title || first.subject || "",
      location: first.location || "",
      notes: first.notes || "",
      selectedParticipants: first.participants || [],
      allDay: !!first.allDay,
      startDate,
      startTime: first.startTime || "09:00",
      endDate: first.endDate || first.endDay || startDate,
      endTime: first.endTime || "10:00",
      recurrence: first.recurrence ?? null,
    });
  }, [switchTab]);

  useEffect(() => {
    const match = location.pathname.match(/^\/approval\/((?:CL|TD|GR|RC)-\d+)$/i);
    if (!match) return;
    const requestId = match[1].toUpperCase();
    setApprovalFocusId(requestId);
    setApprovalsPanelOpen(true);
  }, [location.pathname]);

  const loadEventsCache = () => {
    try {
      const raw = localStorage.getItem(EVENTS_CACHE_KEY);
      if (!raw) return null;
      return JSON.parse(raw);
    } catch { return null; }
  };

  const saveEventsCache = (byMonth, members, famName) => {
    try {
      localStorage.setItem(EVENTS_CACHE_KEY, JSON.stringify({
        eventsByMonth: byMonth,
        familyMembers: members,
        familyName: famName,
        cachedAt: Date.now()
      }));
    } catch { /* ignore quota errors */ }
  };

  // Fetch a single month (one API call per month)
  const fetchMonth = async (year, month) => {
    const res = await fetch(`${apiBase}/api/GetWeek?year=${year}&month=${month}&ts=${Date.now()}`, { cache: "no-store" });
    if (!res.ok) throw new Error(`API ${res.status}: ${await res.text()}`);
    const data = await res.json();
    return data;
  };

  // Get month keys needed for current view
  const getMonthsNeeded = () => {
    const keys = new Set();
    if (viewMode === "week") {
      const start = viewAnchor;
      const end = addDays(viewAnchor, 7);
      let d = new Date(start.getFullYear(), start.getMonth(), 1);
      const endMonth = new Date(end.getFullYear(), end.getMonth(), 1);
      while (d <= endMonth) {
        keys.add(monthKey(d.getFullYear(), d.getMonth() + 1));
        d.setMonth(d.getMonth() + 1);
      }
    } else {
      const m = startOfMonth(viewAnchor);
      keys.add(monthKey(m.getFullYear(), m.getMonth() + 1));
    }
    return Array.from(keys);
  };

  // Merge events from loaded months for display
  const events = (() => {
    const needed = getMonthsNeeded();
    const seen = new Set();
    const merged = [];
    for (const key of needed) {
      const raw = eventsByMonth[key];
      const list = Array.isArray(raw) ? raw : (raw?.events ?? []);
      for (const e of list) {
        const id = e.id + (e.day || "");
        if (!seen.has(id)) {
          seen.add(id);
          merged.push(e);
        }
      }
    }
    return merged;
  })();

  const loadMonths = async (monthKeys, isInitial = false, forceRefresh = false) => {
    if (isInitial) setLoading(true);
    setLoadError(null);
    const toFetch = forceRefresh
      ? monthKeys
      : monthKeys.filter((k) => !eventsByMonth[k]);
    if (toFetch.length === 0) {
      if (isInitial) {
        setHasLoaded(true);
        setLoading(false);
      }
      return true;
    }
    try {
      const results = await Promise.all(
        toFetch.map(async (k) => {
          const [y, m] = k.split("-").map(Number);
          const data = await fetchMonth(y, m);
          return { key: k, data };
        })
      );
      const newMonthData = {};
      results.forEach(({ key, data }) => {
        newMonthData[key] = Array.isArray(data) ? data : (data?.events ?? []);
      });
      setEventsByMonth((prev) => ({ ...prev, ...newMonthData }));
      const firstWithMembers = results.find(
        (r) => Array.isArray(r.data?.members) && r.data.members.length > 0
      );
      const updatedMembers = firstWithMembers ? firstWithMembers.data.members : familyMembers;
      if (firstWithMembers) setFamilyMembers(updatedMembers);
      const firstWithFamilyName = results.find((r) => r.data?.familyName);
      const updatedFamilyName = firstWithFamilyName?.data?.familyName || familyName;
      if (firstWithFamilyName?.data?.familyName) setFamilyName(updatedFamilyName);
      setHasLoaded(true);
      setEventsByMonth((latest) => {
        saveEventsCache(latest, updatedMembers, updatedFamilyName);
        return latest;
      });
      return true;
    } catch (err) {
      console.error("Error fetching events:", err);
      setLoadError(err.message);
      if (!hasLoaded) setEventsByMonth({});
      return false;
    } finally {
      setLoading(false);
    }
  };

  refreshRef.current = { getMonthsNeeded, loadMonths };

  const fetchEvents = async () => {
    const needed = getMonthsNeeded();
    await loadMonths(needed, false, true);
    refreshApprovalBell();
  };

  const hardRefreshEvents = async () => {
    if (refreshing) return;
    setRefreshing(true);
    setRefreshFeedback(null);
    if (refreshFeedbackTimerRef.current) clearTimeout(refreshFeedbackTimerRef.current);
    try {
      await renewSwaSession();
      try {
        localStorage.removeItem(EVENTS_CACHE_KEY);
      } catch {
        // ignore quota / private mode
      }
      const needed = getMonthsNeeded();
      const ok = await loadMonths(needed, false, true);
      refreshApprovalBell();
      if (ok) {
        setRefreshFeedback({ type: "success", message: "Calendar updated" });
        refreshFeedbackTimerRef.current = setTimeout(() => setRefreshFeedback(null), 3000);
      } else {
        setRefreshFeedback({ type: "error", message: "Could not refresh calendar" });
      }
    } catch (err) {
      setRefreshFeedback({
        type: "error",
        message: err.message || "Could not refresh calendar",
      });
    } finally {
      setRefreshing(false);
    }
  };

  const fetchWeather = useCallback(async () => {
    try {
      const res = await fetch(`${apiBase}/api/GetWeather`);
      if (!res.ok) throw new Error(`Weather API ${res.status}`);
      const data = await res.json();
      
      // Map weather by date for quick lookup
      const weatherMap = {};
      data.forEach(day => {
        weatherMap[day.date] = day;
      });
      setWeather(weatherMap);
    } catch (err) {
      console.error("Error fetching weather:", err);
      setWeather({});
    }
  }, [apiBase]);

  const buildMemberIdLookup = () => {
    const map = new Map();
    familyMembers.forEach((m) => {
      if (m.name && m.id && !map.has(m.name)) map.set(m.name, m.id);
    });
    events.forEach(event => {
      if (!Array.isArray(event.participants) || !Array.isArray(event.householdMemberIds)) return;
      event.participants.forEach((name, index) => {
        const id = event.householdMemberIds[index];
        if (name && id && !map.has(name)) {
          map.set(name, id);
        }
      });
    });
    return map;
  };

  const getBaseEventId = (id) => {
    if (!id) return id;
    const [baseId] = id.split("_");
    return baseId;
  };

  const isRecurringInstance = (eventData) => {
    if (!eventData?.id) return false;
    return eventData.id.includes("_");
  };

  const buildEventPayload = (eventData) => {
    const startDay = eventData.startDate || eventData.start?.split("T")[0];
    const endDayRaw = eventData.endDate || eventData.end?.split("T")[0] || startDay;
    const allDay = !!eventData.allDay;
    // Cozi detects all-day when startTime and endTime are both "00:00"
    const startTime = allDay ? "00:00" : (eventData.startTime || "00:00");
    const endTime = allDay ? "00:00" : (eventData.endTime || "23:59");
    const daysDiff = differenceInCalendarDays(parseISO(endDayRaw), parseISO(startDay));
    // dateSpan = calendar-day difference (7th - 6th = 1); same day = 0.
    const endDay = endDayRaw;
    const dateSpan = Math.max(0, Number.isFinite(daysDiff) ? daysDiff : 0);
    const memberLookup = buildMemberIdLookup();
    const rawParticipants = eventData.participants || eventData.selectedParticipants || [];
    // "All" / "all" means no specific members (family default) - exclude from lookup so we send empty attendeeSet
    const participants = rawParticipants.filter((p) => p !== "All" && p !== "all");
    const attendeeSet = participants.length === 0
      ? []
      : participants.map(name => memberLookup.get(name)).filter(Boolean);

    const payload = {
      startDay,
      endDay,
      startTime,
      endTime,
      subject: (eventData.title || "").trim() || "(No title)",
      location: eventData.location || "",
      notes: eventData.notes || "",
      attendeeSet,
      dateSpan: Math.floor(dateSpan)
    };
    if (eventData.recurrence && eventData.recurrence.frequency !== "none") {
      payload.recurrence = eventData.recurrence;
    }
    if (Array.isArray(eventData.reminders) && eventData.reminders.length > 0) {
      payload.reminders = eventData.reminders;
    }
    return payload;
  };

  const createEvent = async (eventData) => {
    const payload = buildEventPayload(eventData);
    const res = await fetch(`${apiBase}/api/CreateEvent`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload)
    });
    if (!res.ok) throw new Error(`CreateEvent ${res.status}: ${await res.text()}`);
    return res.json();
  };

  const updateEvent = async (eventData) => {
    const payload = buildEventPayload(eventData);
    const id = getBaseEventId(eventData.id);
    const selectedDay = eventData.startDate || eventData.start?.split("T")[0] || eventData.day;
    payload.selectedDay = selectedDay;
    if (isRecurringInstance(eventData) && eventData.editScope) {
      payload.extent = eventData.editScope === "series" ? "forward" : eventData.editScope;
      if (payload.extent === "day") delete payload.recurrence;
    } else if (eventData.extent) {
      payload.extent = eventData.extent === "series" ? "forward" : eventData.extent;
    }
    if (Array.isArray(eventData.notifyPersonNames) && eventData.notifyPersonNames.length > 0) {
      const memberLookup = buildMemberIdLookup();
      payload.notifyPersons = eventData.notifyPersonNames.map(name => memberLookup.get(name)).filter(Boolean);
    }
    const res = await fetch(`${apiBase}/api/UpdateEvent`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        id,
        ...payload
      })
    });
    if (!res.ok) throw new Error(`UpdateEvent ${res.status}: ${await res.text()}`);
    const data = await res.json();
    return data;
  };

  const deleteEvent = async (eventData) => {
    const selectedDay = eventData.day || eventData.start?.split("T")[0];
    const extent = eventData.extent || "day";
    const payload = {
      id: getBaseEventId(eventData.id),
      selectedDay,
      extent: extent === "series" ? "forward" : extent
    };
    if (eventData.version != null) payload.version = eventData.version;
    if (Array.isArray(eventData.notifyPersonNames) && eventData.notifyPersonNames.length > 0) {
      const memberLookup = buildMemberIdLookup();
      payload.notifyPersons = eventData.notifyPersonNames.map(name => memberLookup.get(name)).filter(Boolean);
    } else {
      payload.notifyPersons = null;
    }
    const res = await fetch(`${apiBase}/api/DeleteEvent`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload)
    });
    if (!res.ok) throw new Error(`DeleteEvent ${res.status}: ${await res.text()}`);
    return res.json();
  };

  useEffect(() => {
    fetchWeather();
  }, [fetchWeather]);

  // Effect intentionally depends only on viewAnchor/viewMode to avoid redundant loads
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (viewMode === "week") {
      const days = Array.from({ length: 8 }, (_, i) => addDays(viewAnchor, i));
      setWeekDates(days);
    } else {
      setWeekDates([]);
    }
    const today = new Date();
    const curr = monthKey(today.getFullYear(), today.getMonth() + 1);
    const prev = today.getMonth() === 0
      ? monthKey(today.getFullYear() - 1, 12)
      : monthKey(today.getFullYear(), today.getMonth());
    const next = today.getMonth() === 11
      ? monthKey(today.getFullYear() + 1, 1)
      : monthKey(today.getFullYear(), today.getMonth() + 2);
    const needed = getMonthsNeeded();
    const toLoad = [...new Set([prev, curr, next, ...needed])];

    if (!hasLoaded) {
      const cached = loadEventsCache();
      if (cached?.eventsByMonth && Object.keys(cached.eventsByMonth).length > 0) {
        setEventsByMonth(cached.eventsByMonth);
        if (Array.isArray(cached.familyMembers) && cached.familyMembers.length > 0) setFamilyMembers(cached.familyMembers);
        if (cached.familyName) setFamilyName(cached.familyName);
        // Treat cached data as "loaded" so we can render + scroll immediately.
        setHasLoaded(true);
        setLoading(false);
      }
    }

    // Backfill/refresh in the background. If we already have cache, avoid blocking UI with loading spinner.
    loadMonths(toLoad, false, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewAnchor, viewMode]);

  useEffect(() => {
    if (hasAutoScrolledToTodayRef.current) return;
    if (!todayColumnRef.current) return;
    // Scroll ASAP once today's column exists, regardless of network loading.
    hasAutoScrolledToTodayRef.current = true;
    requestAnimationFrame(() => {
      todayColumnRef.current?.scrollIntoView({ behavior: "auto", block: "start" });
    });
  }, [weekDates, viewMode, activeTab]);

  useEffect(() => {
    document.title = familyName;
  }, [familyName]);

  const applyPrefs = useCallback((data) => {
    if (data.theme === "dark" || data.theme === "light") setTheme(data.theme);
    if (typeof data.titleFont === "string") setTitleFont(data.titleFont);
    if (typeof data.calendarFont === "string") setCalendarFont(data.calendarFont);
    if (data.backgroundType === "color" || data.backgroundType === "image") setBackgroundType(data.backgroundType);
    if (typeof data.backgroundColor === "string" && /^#[0-9A-Fa-f]{6}$/.test(data.backgroundColor)) setBackgroundColor(data.backgroundColor);
    if (typeof data.backgroundImage === "string") setBackgroundImage(data.backgroundImage);
    if (typeof data.aiPromptContext === "string") setAiPromptContext(data.aiPromptContext);
    if (typeof data.todoMemberId === "string") setTodoMemberId(data.todoMemberId);
  }, []);

  // Load user preferences: instant from cache, then refresh from API in background
  useEffect(() => {
    let cancelled = false;
    const cached = localStorage.getItem(PREF_CACHE_KEY);
    if (cached) {
      try { applyPrefs(JSON.parse(cached)); } catch { /* invalid cache */ }
    }
    (async () => {
      try {
        const res = await fetch(`${apiBase}/api/GetUserPreferences`, { credentials: "include" });
        if (!res.ok || cancelled) return;
        const data = await res.json().catch(() => ({}));
        applyPrefs(data);
        try {
          localStorage.setItem(PREF_CACHE_KEY, JSON.stringify({
            theme: data.theme || "light",
            titleFont: data.titleFont || "",
            calendarFont: data.calendarFont || "",
            backgroundType: data.backgroundType || "color",
            backgroundColor: data.backgroundColor || "#2c3e50",
            backgroundImage: data.backgroundImage || "",
            aiPromptContext: typeof data.aiPromptContext === "string" ? data.aiPromptContext : "",
            todoMemberId: typeof data.todoMemberId === "string" ? data.todoMemberId : "",
            fetchedAt: Date.now()
          }));
        } catch { /* ignore */ }
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [apiBase, applyPrefs]);

  // Apply theme to document
  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
  }, [theme]);

  // Apply background (color or image) to .dashboard
  useEffect(() => {
    const el = document.querySelector(".dashboard");
    if (!el) return;
    if (backgroundObjectUrlRef.current) {
      URL.revokeObjectURL(backgroundObjectUrlRef.current);
      backgroundObjectUrlRef.current = null;
    }
    // Groceries, Games, and To-Do should always be solid themed background (ignore image settings)
    if (activeTab === "groceries" || activeTab === "games" || activeTab === "todo") {
      el.style.backgroundImage = "none";
      el.style.backgroundRepeat = "";
      el.style.backgroundPosition = "";
      el.style.backgroundSize = "";
      el.style.backgroundAttachment = "";
      el.style.backgroundColor = theme === "dark" ? "#000" : "#fff";
      el.style.color = "";
    } else if (backgroundType === "color") {
      el.style.backgroundImage = "none";
      el.style.backgroundColor = backgroundColor;
      el.style.backgroundSize = "";
      el.style.backgroundAttachment = "";
    } else if (backgroundType === "image" && backgroundImage) {
      const url = backgroundImage.trim();
      const valid = /^https?:\/\//.test(url) || url.startsWith("blob:") || url.startsWith("data:") || url.startsWith("/api/");
      if (valid) {
        // Keep a fallback color behind the image
        el.style.backgroundColor = theme === "dark" ? "#000" : "#fff";
        el.style.backgroundImage = `url("${url}")`;
        // Fill the viewport; crop as needed (no letterboxing)
        el.style.backgroundSize = "cover";
        el.style.backgroundRepeat = "no-repeat";
        el.style.backgroundPosition = "center center";
        el.style.backgroundAttachment = "fixed";
      } else {
        el.style.backgroundImage = "none";
        el.style.backgroundColor = backgroundColor;
      }
    } else {
      el.style.backgroundImage = "none";
      el.style.backgroundColor = backgroundColor;
    }
    return () => {
      if (backgroundObjectUrlRef.current) {
        URL.revokeObjectURL(backgroundObjectUrlRef.current);
        backgroundObjectUrlRef.current = null;
      }
    };
  }, [activeTab, theme, backgroundType, backgroundColor, backgroundImage]);

  const savePreferences = useCallback((updates) => {
    const prefs = {
      theme,
      titleFont,
      calendarFont,
      backgroundType,
      backgroundColor,
      backgroundImage,
      aiPromptContext,
      todoMemberId,
      ...updates
    };
    fetch(`${apiBase}/api/SetUserPreferences`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(prefs)
    }).then(() => {
      try {
        localStorage.setItem(PREF_CACHE_KEY, JSON.stringify({
          ...prefs,
          fetchedAt: Date.now()
        }));
      } catch {
        // ignore
      }
    }).catch(() => {});
  }, [apiBase, theme, titleFont, calendarFont, backgroundType, backgroundColor, backgroundImage, aiPromptContext, todoMemberId]);

  // Resolve which family member the signed-in user is: explicit pref, else auto-match by email.
  const resolvedTodoMemberId = (() => {
    if (todoMemberId && familyMembers.some((m) => m.id === todoMemberId)) return todoMemberId;
    const email = (userEmail || "").trim().toLowerCase();
    if (!email) return "";
    const match = familyMembers.find((m) => m.email && m.email.toLowerCase() === email);
    return match ? match.id : "";
  })();

  const handleChangeTodoMember = useCallback((id) => {
    setTodoMemberId(id);
    savePreferences({ todoMemberId: id });
  }, [savePreferences]);

  const importBackgroundUrl = useCallback((url, onDone) => {
    const trimmedUrl = typeof url === "string" ? url.trim() : "";
    if (!trimmedUrl || !/^https?:\/\//.test(trimmedUrl)) return;
    setBackgroundUploading(true);
    setActionError(null);
    fetch(`${apiBase}/api/UploadBackgroundImage`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ url: trimmedUrl })
    })
      .then((r) => r.json().then((data) => ({ ok: r.ok, data })))
      .then(({ ok, data }) => {
        if (!ok) throw new Error(data?.message || "Could not import image URL");
        const storedUrl = data?.url;
        if (!storedUrl) throw new Error("No URL returned");
        const next = [...(backgroundImageHistory || []), { url: storedUrl, addedAt: new Date().toISOString() }];
        setBackgroundImageHistory(next);
        setBackgroundImage(storedUrl);
        savePreferences({ backgroundImage: storedUrl });
        return fetch(`${apiBase}/api/UpdateFamilyBackgroundImages`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ imageHistory: next })
        });
      })
      .then(() => {
        if (typeof onDone === "function") onDone();
      })
      .catch((err) => {
        setActionError(err?.message || "Could not import image URL");
      })
      .finally(() => setBackgroundUploading(false));
  }, [apiBase, backgroundImageHistory, savePreferences]);

  // Load signed-in user (email) for settings dropdown label
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch("/.auth/me", { credentials: "include" });
        if (!res.ok || cancelled) return;
        const data = await res.json().catch(() => null);
        const principal = parseAuthMePayload(data);
        const email = getPrincipalEmail(principal);
        if (email) setUserEmail(email);
        const roles = Array.isArray(principal?.userRoles)
          ? principal.userRoles.map((r) => String(r).toLowerCase())
          : [];
        setIsAdmin(roles.includes("admin"));
      } catch {
        // ignore
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Keep Azure SWA auth session alive (default ~8h cookie); important for wallboard displays.
  useEffect(() => {
    const refreshSession = () => {
      renewSwaSession();
    };
    refreshSession();
    const intervalId = setInterval(refreshSession, AUTH_REFRESH_MS);
    const onVisible = () => {
      if (document.visibilityState !== "visible") return;
      refreshSession();
      if (hasLoaded) {
        const needed = refreshRef.current.getMonthsNeeded();
        refreshRef.current.loadMonths(needed, false, true);
        refreshApprovalBell();
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(intervalId);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [hasLoaded, refreshApprovalBell]);

  useEffect(() => {
    return () => {
      if (refreshFeedbackTimerRef.current) clearTimeout(refreshFeedbackTimerRef.current);
    };
  }, []);

  useEffect(() => {
    const close = (e) => {
      if (gearMenuRef.current && !gearMenuRef.current.contains(e.target)) setGearMenuOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setGearMenuOpen(false); };
    document.addEventListener("click", close);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("click", close);
      document.removeEventListener("keydown", onKey);
    };
  }, [gearMenuOpen]);

  // Ctrl+K / Cmd+K to open search modal; Escape to close
  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "k") {
        e.preventDefault();
        setSearchModalOpen(true);
        setSearchQuery("");
        setGearMenuOpen(false);
        setTimeout(() => searchInputRef.current?.focus(), 0);
      }
      if (e.key === "Escape" && searchModalOpen) setSearchModalOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [searchModalOpen]);

  // When gear opens and background is image, fetch shared image history
  useEffect(() => {
    if (!gearMenuOpen || backgroundType !== "image") return;
    setBackgroundHistoryLoading(true);
    fetch(`${apiBase}/api/GetFamilyBackgroundImages`, { credentials: "include" })
      .then((r) => r.ok ? r.json() : { imageHistory: [] })
      .then((d) => setBackgroundImageHistory(d.imageHistory || []))
      .catch(() => setBackgroundImageHistory([]))
      .finally(() => setBackgroundHistoryLoading(false));
  }, [gearMenuOpen, backgroundType, apiBase]);

  useEffect(() => {
    if (!weekPickerOpen && !monthPickerOpen) return;
    const close = (e) => {
      if (viewTitleRef.current && !viewTitleRef.current.contains(e.target)) {
        setWeekPickerOpen(false);
        setMonthPickerOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === "Escape") {
        setWeekPickerOpen(false);
        setMonthPickerOpen(false);
      }
    };
    document.addEventListener("click", close);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("click", close);
      document.removeEventListener("keydown", onKey);
    };
  }, [weekPickerOpen, monthPickerOpen]);

  // Background refresh every hour (no loading UI); skip when tab is hidden
  useEffect(() => {
    if (!hasLoaded) return;
    const POLL_MS = 60 * 60 * 1000;
    const id = setInterval(() => {
      if (document.visibilityState === "hidden") return;
      const needed = refreshRef.current.getMonthsNeeded();
      refreshRef.current.loadMonths(needed, false, true);
    }, POLL_MS);
    return () => clearInterval(id);
  }, [hasLoaded]);

  // Extract unique participants with their colors from all events, or use full family when API provides it
  const getLegendItems = () => {
    if (familyMembers.length > 0) {
      return familyMembers
        .map((m) => ({ name: m.name, color: m.color }))
        .sort((a, b) => a.name.localeCompare(b.name));
    }
    const participantMap = new Map();
    events.forEach(event => {
      event.participants.forEach((name, index) => {
        const color = event.participantColors[index];
        if (!participantMap.has(name)) {
          participantMap.set(name, color);
        }
      });
    });
    return Array.from(participantMap.entries())
      .map(([name, color]) => ({ name, color }))
      .sort((a, b) => a.name.localeCompare(b.name));
  };

  // Extract time in minutes from ISO datetime string
  const getTimeInMinutes = (isoString) => {
    if (!isoString) return Infinity;
    const date = parseISO(isoString);
    return date.getHours() * 60 + date.getMinutes();
  };

  // get events for a specific day (including multi-day events that span this day), sorted
  const eventsForDay = (day) => {
    const dayStr = format(day, "yyyy-MM-dd");
    const dayEvents = events.filter((e) => {
      const endDay = e.endDay || e.day;
      return dayStr >= e.day && dayStr <= endDay;
    });
    
    // Sort: birthday events first, then all-day events, then by start time
    return dayEvents.sort((a, b) => {
      const aIsBirthday = a.title && a.title.toLowerCase().includes("birthday");
      const bIsBirthday = b.title && b.title.toLowerCase().includes("birthday");
      
      // Birthday events always come first
      if (aIsBirthday && !bIsBirthday) return -1;
      if (!aIsBirthday && bIsBirthday) return 1;
      
      // Then sort by all-day vs timed events
      if (a.allDay && !b.allDay) return -1;
      if (!a.allDay && b.allDay) return 1;
      if (a.allDay && b.allDay) return 0;
      
      // Sort by start time for timed events
      const timeA = getTimeInMinutes(a.start);
      const timeB = getTimeInMinutes(b.start);
      return timeA - timeB;
    });
  };

  // Check if we need a divider after this event (last all-day event before timed events)
  const needsDivider = (dayEvents, currentIndex) => {
    const currentEvent = dayEvents[currentIndex];
    const nextEvent = dayEvents[currentIndex + 1];
    
    return currentEvent.allDay && nextEvent && !nextEvent.allDay;
  };

  const formatMultiDayRange = (event) => {
    if (!event.endDay || event.endDay === event.day) return null;
    return `${format(parseISO(event.day), "MMM d")} ${SYM.enDash} ${format(parseISO(event.endDay), "MMM d")}`;
  };

  // hard-stops gradient: each color gets equal width, no fade between
  const eventBackground = (colors) => {
    if (!colors?.length) return "#ccc";
    if (colors.length === 1) return colors[0];
    const n = colors.length;
    const slice = 100 / n;
    const stops = colors.map((c, i) => `${c} ${(i * slice).toFixed(2)}% ${((i + 1) * slice).toFixed(2)}%`);
    return `linear-gradient(135deg, ${stops.join(", ")})`;
  };

  // Handle day click to create new event
  const handleDayClick = (day) => {
    setSelectedDate(day);
    setSelectedEvent(null); // null indicates new event
  };

  // Drag-and-drop: move event to a different day (preserves time)
  const handleDragStart = (e, event) => {
    setDraggedEventId(event.id);
    e.dataTransfer.setData("application/json", JSON.stringify({
      id: event.id,
      day: event.day,
      start: event.start,
      end: event.end,
      allDay: event.allDay,
      title: event.title,
      location: event.location,
      notes: event.notes,
      participants: event.participants,
      householdMemberIds: event.householdMemberIds,
      participantColors: event.participantColors,
      version: event.version
    }));
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e, day) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDropTargetDay(format(day, "yyyy-MM-dd"));
  };

  const handleDragLeave = () => {
    setDropTargetDay(null);
  };

  const handleDrop = async (e, targetDay) => {
    e.preventDefault();
    setDropTargetDay(null);
    setDraggedEventId(null);
    const raw = e.dataTransfer.getData("application/json");
    if (!raw) return;
    let eventData;
    try {
      eventData = JSON.parse(raw);
    } catch {
      return;
    }
    const targetDayStr = format(targetDay, "yyyy-MM-dd");
    if (eventData.day === targetDayStr) return; // Same day, no-op

    const baseId = getBaseEventId(eventData.id);
    const startDate = parseISO(eventData.start);
    const endDate = parseISO(eventData.end);
    const dateSpan = Math.max(0, differenceInCalendarDays(endDate, startDate));
    const allDay = !!eventData.allDay;

    try {
      setActionError(null);
      await updateEvent({
        id: baseId,
        startDate: targetDayStr,
        endDate: dateSpan > 0 ? format(addDays(parseISO(targetDayStr), dateSpan), "yyyy-MM-dd") : targetDayStr,
        startTime: format(startDate, "HH:mm"),
        endTime: format(endDate, "HH:mm"),
        allDay,
        title: eventData.title,
        location: eventData.location || "",
        notes: eventData.notes || "",
        selectedParticipants: eventData.participants || []
      });
      await fetchEvents();
    } catch (err) {
      console.error("Error moving event:", err);
      setActionError(err.message);
    }
  };

  const handleDragEnd = () => {
    setDraggedEventId(null);
    setDropTargetDay(null);
  };

  const goPrev = () => {
    if (viewMode === "week") {
      setViewAnchor((a) => addWeeks(a, -1));
    } else {
      setViewAnchor((a) => addMonths(startOfMonth(a), -1));
    }
  };

  const goNext = () => {
    if (viewMode === "week") {
      setViewAnchor((a) => addWeeks(a, 1));
    } else {
      setViewAnchor((a) => addMonths(startOfMonth(a), 1));
    }
  };

  const SWIPE_THRESHOLD = 160;
  const SWIPE_MAX_VERTICAL_DRIFT = 90;
  const handleSwipeStart = (e) => {
    if (e.touches?.length) {
      swipeStartX.current = e.touches[0].clientX;
      swipeStartY.current = e.touches[0].clientY;
      swipeStartedOnEvent.current = !!e.target?.closest?.(".event");
    }
  };
  const handleSwipeEnd = (e) => {
    if (swipeStartX.current == null || !e.changedTouches?.length) return;
    if (swipeStartedOnEvent.current) {
      swipeStartX.current = null;
      swipeStartY.current = null;
      swipeStartedOnEvent.current = false;
      return;
    }
    const delta = e.changedTouches[0].clientX - swipeStartX.current;
    const deltaY = Math.abs(e.changedTouches[0].clientY - (swipeStartY.current ?? e.changedTouches[0].clientY));
    swipeStartX.current = null;
    swipeStartY.current = null;
    if (deltaY > SWIPE_MAX_VERTICAL_DRIFT || Math.abs(delta) <= deltaY) return;
    if (delta > SWIPE_THRESHOLD) goPrev();
    else if (delta < -SWIPE_THRESHOLD) goNext();
  };

  const goToToday = () => {
    const today = new Date();
    if (viewMode === "week") {
      setViewAnchor(startOfWeek(today, { weekStartsOn: 0 }));
    } else {
      setViewAnchor(startOfMonth(today));
    }
  };

  // Build month calendar grid (weeks of 7 days, with padding from prev/next month)
  const getMonthGrid = () => {
    const monthStart = startOfMonth(viewAnchor);
    const monthEnd = endOfMonth(viewAnchor);
    const gridStart = startOfWeek(monthStart, { weekStartsOn: 0 });
    const gridEnd = endOfWeek(monthEnd, { weekStartsOn: 0 });
    const days = eachDayOfInterval({ start: gridStart, end: gridEnd });
    const weeks = [];
    for (let i = 0; i < days.length; i += 7) {
      weeks.push(days.slice(i, i + 7));
    }
    return weeks;
  };

  const getPickerCalendarGrid = (monthDate) => {
    const monthStart = startOfMonth(monthDate);
    const monthEnd = endOfMonth(monthDate);
    const gridStart = startOfWeek(monthStart, { weekStartsOn: 0 });
    const gridEnd = endOfWeek(monthEnd, { weekStartsOn: 0 });
    const days = eachDayOfInterval({ start: gridStart, end: gridEnd });
    const weeks = [];
    for (let i = 0; i < days.length; i += 7) weeks.push(days.slice(i, i + 7));
    return weeks;
  };

  const MONTH_NAMES = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

  // Split days into two rows of 4 (week view)
  const firstRow = weekDates.slice(0, 4);
  const secondRow = weekDates.slice(4, 8);
  const monthWeeks = viewMode === "month" ? getMonthGrid() : [];

  const legendItems = getLegendItems();
  const textColorForBg = (hex) => {
    const n = parseInt(hex.slice(1), 16);
    const r = (n >> 16) & 0xff, g = (n >> 8) & 0xff, b = n & 0xff;
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance < 0.5 ? "#fff" : "#333";
  };

  const viewTitle =
    viewMode === "week"
      ? `Week of ${format(viewAnchor, "MMM d, yyyy")}`
      : format(viewAnchor, "MMMM yyyy");

  return (
    <div className={`dashboard dashboard--${activeTab}${theme === "dark" ? " theme-dark" : ""}`}>
      <UpdateBanner />
      <ApprovalBell
        apiBase={apiBase}
        refreshKey={approvalBellRefresh}
        visible={activeTab !== "games"}
        slotId={
          activeTab === "todo"
            ? [APPROVAL_BELL_SLOTS.todoMobile, APPROVAL_BELL_SLOTS.todoDesktop]
            : APPROVAL_BELL_SLOTS[activeTab]
        }
        onOpen={() => {
          setApprovalsPanelOpen(true);
          setApprovalFocusId(null);
        }}
      />
      <ApprovalsPanel
        apiBase={apiBase}
        open={approvalsPanelOpen}
        refreshKey={approvalBellRefresh}
        onClose={() => {
          setApprovalsPanelOpen(false);
          setApprovalFocusId(null);
          if (location.pathname.startsWith("/approval/")) navigate("/", { replace: true });
        }}
        focusRequestId={approvalFocusId}
        onApproveCalendar={openCalendarApproval}
        onRefreshBell={refreshApprovalBell}
        participants={participantNames.length ? participantNames : getLegendItems().map((item) => item.name)}
      />
      <AssistantPanel
        apiBase={apiBase}
        open={assistantOpen}
        onClose={() => setAssistantOpen(false)}
        onOpenApproval={(requestId) => {
          setApprovalFocusId(requestId);
          setApprovalsPanelOpen(true);
          refreshApprovalBell();
        }}
        onRefreshBell={refreshApprovalBell}
      />
      <div
        className="calendars-screen"
        style={{ display: activeTab === "calendars" ? "block" : "none" }}
      >
        <header className="dashboard-header">
          <div id={APPROVAL_BELL_SLOTS.calendars} className="approval-bell-slot" aria-hidden="true" />
          <h1 style={titleFont ? { fontFamily: `"${titleFont}", sans-serif` } : undefined}>
            {familyName}
          </h1>
          {refreshFeedback && (
            <div className={`refresh-feedback refresh-feedback-${refreshFeedback.type}`} role="status">
              {refreshFeedback.message}
            </div>
          )}
          <div className="dashboard-header-actions">
            <div ref={gearMenuRef} style={{ position: "relative", display: "flex", alignItems: "center" }}>
          <button
            type="button"
            className="assistant-header-btn"
            onClick={() => setAssistantOpen(true)}
            aria-label="Open assistant"
            title="Assistant"
          >
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </svg>
          </button>
          <button
            type="button"
            className="refresh-btn"
            onClick={() => hardRefreshEvents()}
            disabled={refreshing}
            aria-label="Refresh calendar"
            aria-busy={refreshing}
            title="Refresh calendar"
          >
            <span className={`refresh-btn-icon${refreshing ? " refresh-btn-icon-spinning" : ""}`} aria-hidden="true">
              {SYM.refresh}
            </span>
          </button>
          <button
            type="button"
            className={`view-toggle-btn ${viewMode === "week" ? "active" : ""}`}
            onClick={() => {
              setViewMode("week");
              setViewAnchor(startOfWeek(viewAnchor, { weekStartsOn: 0 }));
            }}
            aria-label="Week view"
            title="Week view"
          >
            W
          </button>
          <button
            type="button"
            className={`view-toggle-btn ${viewMode === "month" ? "active" : ""}`}
            onClick={() => {
              setViewMode("month");
              setViewAnchor(startOfMonth(viewAnchor));
            }}
            aria-label="Month view"
            title="Month view"
          >
            M
          </button>
          <button
            type="button"
            className="settings-btn"
            onClick={() => {
              setSearchModalOpen(true);
              setSearchQuery("");
              setTimeout(() => searchInputRef.current?.focus(), 0);
            }}
            aria-label="Search events"
            title="Search events (Ctrl+K)"
          >
            {SYM.search}
          </button>
          <button
            type="button"
            className="settings-btn"
            onClick={() => setGearMenuOpen((o) => !o)}
            aria-label="Menu"
            aria-haspopup="true"
            aria-expanded={gearMenuOpen}
            title="Menu"
          >
            {SYM.gear}
          </button>
          {gearMenuOpen && (
            <div className="gear-dropdown">
              {userEmail && (
                <div className="gear-dropdown-user">
                  <span className="gear-dropdown-user-label" title={userEmail}>{userEmail}</span>
                </div>
              )}
              <div className="gear-dropdown-row">
                <span className="gear-dropdown-label">Theme</span>
                <div className="gear-dropdown-theme-switch">
                  <button
                    type="button"
                    className={`gear-theme-btn ${theme === "light" ? "active" : ""}`}
                    onClick={() => {
                      setTheme("light");
                      savePreferences({ theme: "light" });
                    }}
                  >
                    Light
                  </button>
                  <button
                    type="button"
                    className={`gear-theme-btn ${theme === "dark" ? "active" : ""}`}
                    onClick={() => {
                      setTheme("dark");
                      savePreferences({ theme: "dark" });
                    }}
                  >
                    Dark
                  </button>
                </div>
              </div>
              <div className="gear-dropdown-row">
                <span className="gear-dropdown-label">Title Font</span>
                <button
                  type="button"
                  className="gear-font-preview-btn"
                  style={titleFont ? { fontFamily: `"${titleFont}", sans-serif` } : undefined}
                  onClick={() => setFontPickerTarget("title")}
                >
                  {titleFont || "Default"}
                </button>
              </div>
              <div className="gear-dropdown-row">
                <span className="gear-dropdown-label">Calendar Font</span>
                <button
                  type="button"
                  className="gear-font-preview-btn"
                  style={calendarFont ? { fontFamily: `"${calendarFont}", sans-serif` } : undefined}
                  onClick={() => setFontPickerTarget("calendar")}
                >
                  {calendarFont || "Default"}
                </button>
              </div>
              <div className="gear-dropdown-row">
                <span className="gear-dropdown-label">Background</span>
                <div className="gear-dropdown-theme-switch">
                  <button
                    type="button"
                    className={`gear-theme-btn ${backgroundType === "color" ? "active" : ""}`}
                    onClick={() => {
                      setBackgroundType("color");
                      savePreferences({ backgroundType: "color" });
                    }}
                  >
                    Color
                  </button>
                  <button
                    type="button"
                    className={`gear-theme-btn ${backgroundType === "image" ? "active" : ""}`}
                    onClick={() => {
                      setBackgroundType("image");
                      savePreferences({ backgroundType: "image" });
                    }}
                  >
                    Image
                  </button>
                </div>
              </div>
              {backgroundType === "color" && (
                <div className="gear-dropdown-row">
                  <span className="gear-dropdown-label">Color</span>
                  <input
                    type="color"
                    value={backgroundColor}
                    onChange={(e) => {
                      const v = e.target.value;
                      setBackgroundColor(v);
                      savePreferences({ backgroundColor: v });
                    }}
                    className="gear-color-input"
                    aria-label="Background color"
                  />
                </div>
              )}
              {backgroundType === "image" && (
                <div className="gear-dropdown-section">
                  <button
                    type="button"
                    className="gear-dropdown-item gear-gallery-btn"
                    onClick={() => {
                      setGearMenuOpen(false);
                      setGalleryModalOpen(true);
                      setBackgroundHistoryLoading(true);
                      fetch(`${apiBase}/api/GetFamilyBackgroundImages`, { credentials: "include" })
                        .then((r) => r.ok ? r.json() : { imageHistory: [] })
                        .then((d) => setBackgroundImageHistory(d.imageHistory || []))
                        .catch(() => setBackgroundImageHistory([]))
                        .finally(() => setBackgroundHistoryLoading(false));
                    }}
                  >
                    Select from gallery
                  </button>
                  <input
                    type="file"
                    accept="image/*"
                    ref={backgroundFileInputRef}
                    style={{ display: "none" }}
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      e.target.value = "";
                      if (!file || !file.type.startsWith("image/")) return;
                      setBackgroundUploading(true);
                      const reader = new FileReader();
                      reader.onload = () => {
                        const dataUrl = reader.result;
                        const base64 = typeof dataUrl === "string" && dataUrl.includes(",") ? dataUrl.slice(dataUrl.indexOf(",") + 1) : null;
                        if (!base64) {
                          setBackgroundUploading(false);
                          return;
                        }
                        fetch(`${apiBase}/api/UploadBackgroundImage`, {
                          method: "POST",
                          headers: { "Content-Type": "application/json" },
                          credentials: "include",
                          body: JSON.stringify({ base64, contentType: file.type })
                        })
                          .then((r) => r.json())
                          .then((data) => {
                            const url = data?.url;
                            if (!url) throw new Error(data?.message || "No URL returned");
                            const next = [...(backgroundImageHistory || []), { url, addedAt: new Date().toISOString() }];
                            setBackgroundImageHistory(next);
                            setBackgroundImage(url);
                            savePreferences({ backgroundImage: url });
                            return fetch(`${apiBase}/api/UpdateFamilyBackgroundImages`, {
                              method: "POST",
                              headers: { "Content-Type": "application/json" },
                              credentials: "include",
                              body: JSON.stringify({ imageHistory: next })
                            });
                          })
                          .catch(() => {})
                          .finally(() => setBackgroundUploading(false));
                      };
                      reader.readAsDataURL(file);
                    }}
                  />
                  <div className="gear-add-url">
                    <input
                      type="url"
                      placeholder={`https://${SYM.ellipsis} image URL`}
                      className="gear-url-input"
                      id="background-url-input"
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          const input = e.target;
                          const url = (input.value || "").trim();
                          importBackgroundUrl(url, () => { input.value = ""; });
                        }
                      }}
                    />
                    <button
                      type="button"
                      className="gear-add-url-btn"
                      disabled={backgroundUploading}
                      onClick={() => {
                        const input = document.getElementById("background-url-input");
                        if (!input) return;
                        const url = (input.value || "").trim();
                        importBackgroundUrl(url, () => { input.value = ""; });
                      }}
                    >
                      {backgroundUploading ? withEllipsis("Importing") : "Add URL"}
                    </button>
                    <button
                      type="button"
                      className="gear-add-url-btn"
                      onClick={() => backgroundFileInputRef.current?.click()}
                      disabled={backgroundUploading}
                    >
                      {backgroundUploading ? withEllipsis("Uploading") : "Upload"}
                    </button>
                  </div>
                  <p className="gear-dropdown-hint">Click an image to set as background. Add URL or Upload image; use Generate on main screen for AI.</p>
                </div>
              )}
              <button
                type="button"
                className="gear-dropdown-item"
                onClick={() => {
                  setGearMenuOpen(false);
                  setSearchModalOpen(true);
                  setSearchQuery("");
                  setTimeout(() => searchInputRef.current?.focus(), 0);
                }}
              >
                Search events
              </button>
              <button
                type="button"
                className="gear-dropdown-item"
                onClick={() => {
                  setGearMenuOpen(false);
                  setGenerateBackgroundOpen(true);
                }}
              >
                Generate background
              </button>
              {isAdmin && (
                <button
                  type="button"
                  className="gear-dropdown-item"
                  onClick={() => {
                    setGearMenuOpen(false);
                    setGmailTestingOpen(true);
                  }}
                >
                  Gmail testing
                </button>
              )}
              <button
                type="button"
                className="gear-dropdown-item"
                onClick={() => {
                  setGearMenuOpen(false);
                  setSchoolSettingsOpen(true);
                }}
              >
                School settings
              </button>
              <div className="gear-dropdown-row">
                <span className="gear-dropdown-label">AI prompt context</span>
                <textarea
                  className="gear-ai-context-input"
                  placeholder="e.g. Prefer watercolors; family name is Smith"
                  value={aiPromptContext}
                  onChange={(e) => setAiPromptContext(e.target.value)}
                  onBlur={(e) => savePreferences({ aiPromptContext: e.target.value })}
                  rows={2}
                  aria-label="Additional context for AI"
                />
                <p className="gear-dropdown-hint">Added to Smart Add and background generation. Leave empty for default.</p>
              </div>
              <button
                type="button"
                className="gear-dropdown-item"
                onClick={() => {
                  setGearMenuOpen(false);
                  setSettingsOpen(true);
                }}
              >
                Edit credentials
              </button>
              <a
                href={buildLogoutUrl("/login")}
                className="gear-dropdown-item"
                onClick={() => setGearMenuOpen(false)}
              >
                Sign out
              </a>
            </div>
          )}
            </div>
          </div>
        </header>

      <GmailTestingModal
        apiBase={apiBase}
        open={gmailTestingOpen}
        onClose={() => setGmailTestingOpen(false)}
        onPollComplete={refreshApprovalBell}
      />
      <SchoolSettingsModal apiBase={apiBase} open={schoolSettingsOpen} onClose={() => setSchoolSettingsOpen(false)} familyMembers={familyMembers} />

      {generateBackgroundOpen && (
        <div className="settings-overlay" onClick={() => !generateLoading && setGenerateBackgroundOpen(false)}>
          <div className="settings-modal generate-modal" onClick={(e) => e.stopPropagation()}>
            <div className="settings-modal-header">
              <h2>Generate background</h2>
              <button type="button" className="settings-close" onClick={() => !generateLoading && setGenerateBackgroundOpen(false)} aria-label="Close">{SYM.close}</button>
            </div>
            <div className="settings-modal-body">
              {!generateResultUrl ? (
                <>
                  <p className="gear-dropdown-hint">Describe the image you want. We&apos;ll add &quot;high resolution wallpaper style&quot; and generate it with Grok.</p>
                  <textarea
                    placeholder="e.g. A peaceful lake at sunset with mountains"
                    value={generatePrompt}
                    onChange={(e) => setGeneratePrompt(e.target.value)}
                    className="generate-prompt-input"
                    rows={3}
                    disabled={generateLoading}
                  />
                  {generateError && <p className="settings-error">{generateError}</p>}
                  <button
                    type="button"
                    className="settings-submit"
                    disabled={generateLoading || !generatePrompt.trim()}
                    onClick={() => {
                      setGenerateError(null);
                      setGenerateLoading(true);
                        fetch(`${apiBase}/api/GenerateBackgroundWithGrok`, {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        credentials: "include",
                        body: JSON.stringify({
                          prompt: generatePrompt.trim(),
                          ...(aiPromptContext?.trim() ? { promptContext: aiPromptContext.trim() } : {})
                        })
                      })
                        .then((r) => r.json())
                        .then((data) => {
                          if (data?.url) {
                            setGenerateResultUrl(data.url);
                          } else {
                            setGenerateError(data?.message || "No image returned");
                          }
                        })
                        .catch((err) => setGenerateError(err?.message || "Request failed"))
                        .finally(() => setGenerateLoading(false));
                    }}
                  >
                    {generateLoading ? withEllipsis("Generating") : "Generate"}
                  </button>
                </>
              ) : (
                <>
                  <div className="generate-result-preview">
                    <img
                      src={generateResultUrl}
                      alt="Generated background preview"
                      className="generate-result-img"
                      onError={(e) => { console.error("Preview image failed to load:", generateResultUrl); e.target.style.display = "none"; }}
                    />
                  </div>
                  <div className="generate-result-actions">
                    <button
                      type="button"
                      className="settings-submit"
                      onClick={() => {
                        setBackgroundType("image");
                        setBackgroundImage(generateResultUrl);
                        savePreferences({ backgroundType: "image", backgroundImage: generateResultUrl });
                        const next = [...(backgroundImageHistory || []), { url: generateResultUrl, addedAt: new Date().toISOString() }];
                        setBackgroundImageHistory(next);
                        fetch(`${apiBase}/api/UpdateFamilyBackgroundImages`, {
                          method: "POST",
                          headers: { "Content-Type": "application/json" },
                          credentials: "include",
                          body: JSON.stringify({ imageHistory: next })
                        }).catch(() => {});
                        setGenerateBackgroundOpen(false);
                        setGenerateResultUrl(null);
                        setGeneratePrompt("");
                      }}
                    >
                      Accept
                    </button>
                    <button
                      type="button"
                      className="settings-btn-secondary"
                      onClick={() => {
                        setGenerateResultUrl(null);
                        setGenerateError(null);
                      }}
                    >
                      Modify & Regenerate
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Gallery modal - select from all previously used images */}
      {galleryModalOpen && (
        <div className="settings-overlay" onClick={() => setGalleryModalOpen(false)}>
          <div className="settings-modal gallery-modal" onClick={(e) => e.stopPropagation()}>
            <div className="settings-modal-header">
              <h2>Select from gallery</h2>
              <button type="button" className="settings-close" onClick={() => setGalleryModalOpen(false)} aria-label="Close">{SYM.close}</button>
            </div>
            <div className="settings-modal-body gallery-modal-body">
              {backgroundHistoryLoading ? (
                <p className="gear-dropdown-hint">{withEllipsis("Loading")}</p>
              ) : (backgroundImageHistory || []).length === 0 ? (
                <p className="gear-dropdown-hint">No images in gallery yet. Upload an image or generate one from the menu.</p>
              ) : (
                <div className="gallery-grid">
                  {(backgroundImageHistory || []).map((entry, i) => (
                    <div key={`${entry.url}-${i}`} className="gallery-grid-item-wrap">
                      <button
                        type="button"
                        className="gallery-grid-item"
                        onClick={() => {
                          setBackgroundImage(entry.url);
                          savePreferences({ backgroundImage: entry.url });
                          setBackgroundType("image");
                          savePreferences({ backgroundType: "image" });
                          setGalleryModalOpen(false);
                        }}
                        title="Set as background"
                      >
                        <img src={entry.url} alt="" className="gallery-grid-item-img" />
                      </button>
                      <button
                        type="button"
                        className="gallery-grid-item-delete"
                        aria-label="Delete from gallery"
                        title="Delete"
                        onClick={(e) => {
                          e.stopPropagation();
                          const next = (backgroundImageHistory || []).filter((_, j) => j !== i);
                          setBackgroundImageHistory(next);
                          fetch(`${apiBase}/api/UpdateFamilyBackgroundImages`, {
                            method: "POST",
                            headers: { "Content-Type": "application/json" },
                            credentials: "include",
                            body: JSON.stringify({ imageHistory: next })
                          }).catch(() => {});
                        }}
                      >
                        {SYM.close}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Search events modal */}
      {searchModalOpen && (
        <div className="settings-overlay" onClick={() => setSearchModalOpen(false)}>
          <div className="settings-modal search-modal" onClick={(e) => e.stopPropagation()}>
            <div className="settings-modal-header">
              <h2>Search events</h2>
              <button type="button" className="settings-close" onClick={() => setSearchModalOpen(false)} aria-label="Close">{SYM.close}</button>
            </div>
            <div className="settings-modal-body search-modal-body">
              <input
                ref={searchInputRef}
                type="text"
                className="search-modal-input"
                placeholder={`Search by title, location, notes, participants${SYM.ellipsis}`}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                aria-label="Search events"
              />
              <div className="search-modal-results">
                {(() => {
                  const q = searchQuery.trim().toLowerCase();
                  const results = q
                    ? events.filter((e) => {
                        const title = (e.title || "").toLowerCase();
                        const location = (e.location || "").toLowerCase();
                        const notes = (e.notes || "").toLowerCase();
                        const participantsStr = Array.isArray(e.participants) ? e.participants.join(" ").toLowerCase() : "";
                        const dayStr = e.day ? format(parseISO(e.day), "MMMM d yyyy").toLowerCase() : "";
                        return title.includes(q) || location.includes(q) || notes.includes(q) || participantsStr.includes(q) || dayStr.includes(q);
                      })
                    : [];
                  if (!q) {
                    return <p className="gear-dropdown-hint">Type to search loaded events.</p>;
                  }
                  if (events.length === 0) {
                    return <p className="gear-dropdown-hint">No events loaded. Refresh the calendar first.</p>;
                  }
                  if (results.length === 0) {
                    return <p className="gear-dropdown-hint">No events match &quot;{searchQuery.trim()}&quot;.</p>;
                  }
                  return (
                    <ul className="search-modal-list" aria-label="Search results">
                      {results.map((e) => (
                        <li key={e.id + (e.day || "")}>
                          <button
                            type="button"
                            className="search-modal-result-btn"
                            onClick={() => {
                              setSelectedEvent(e);
                              setSelectedDate(parseISO(e.day));
                              setSearchModalOpen(false);
                              setSearchQuery("");
                            }}
                          >
                            <span className="search-modal-result-title">{e.title || "(No title)"}</span>
                            <span className="search-modal-result-meta">
                              {e.day ? format(parseISO(e.day), "MMM d, yyyy") : ""}
                              {e.allDay ? ` ${SYM.middot} All day` : e.start ? ` ${SYM.middot} ${formatTimeRange(e)}` : ""}
                            </span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  );
                })()}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Font picker modal */}
      {fontPickerTarget && (
        <div className="settings-overlay" onClick={() => setFontPickerTarget(null)}>
          <div className="settings-modal font-picker-modal" onClick={(e) => e.stopPropagation()}>
            <div className="settings-modal-header">
              <h2>{fontPickerTarget === "title" ? "Choose a title font" : "Choose a calendar font"}</h2>
              <button type="button" className="settings-close" onClick={() => setFontPickerTarget(null)} aria-label="Close">{SYM.close}</button>
            </div>
            <div className="settings-modal-body font-picker-body">
              <p className="gear-dropdown-hint">
                {fontPickerTarget === "title"
                  ? "Select a font for the family name header."
                  : "Select a font for the calendar grid, events, and navigation."}
              </p>
              <div className="font-picker-grid">
                {FONT_OPTIONS.map((f) => {
                  const activeFont = fontPickerTarget === "title" ? titleFont : calendarFont;
                  const previewText = fontPickerTarget === "title"
                    ? familyName
                    : "Wed 15 · 9:00 Soccer practice";
                  return (
                    <button
                      key={f.value || "_default"}
                      type="button"
                      className={`font-picker-item ${activeFont === f.value ? "active" : ""}`}
                      style={f.value ? { fontFamily: `"${f.value}", sans-serif` } : undefined}
                      onClick={() => {
                        if (fontPickerTarget === "title") {
                          setTitleFont(f.value);
                          savePreferences({ titleFont: f.value });
                        } else {
                          setCalendarFont(f.value);
                          savePreferences({ calendarFont: f.value });
                        }
                        setFontPickerTarget(null);
                      }}
                    >
                      <span className="font-picker-preview">{previewText}</span>
                      <span className="font-picker-name">{f.name}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Settings modal */}
      {settingsOpen && (
        <div className="settings-overlay" onClick={() => setSettingsOpen(false)}>
          <div className="settings-modal" onClick={(e) => e.stopPropagation()}>
            <div className="settings-modal-header">
              <h2>Edit credentials</h2>
              <button type="button" className="settings-close" onClick={() => setSettingsOpen(false)} aria-label="Close">{SYM.close}</button>
            </div>
            <div className="settings-modal-body">
              <div className="settings-section">
                <label htmlFor="cozi-username">Cozi Username</label>
                <p className="settings-hint">Enter your Cozi account email and password. Test connection, then save. Credentials are stored in Azure Key Vault when the backend is configured for it.</p>
                <input
                  id="cozi-username"
                  type="text"
                  placeholder="Username"
                  value={coziUsername}
                  onChange={(e) => {
                    setCoziUsername(e.target.value);
                    setCoziTestSuccess(false);
                    setCoziTestError(null);
                    setCoziSaveSuccess(false);
                    setCoziSaveError(null);
                  }}
                  className="settings-input"
                  autoComplete="username email"
                />
                <label htmlFor="cozi-password">Cozi Password</label>
                <input
                  id="cozi-password"
                  type="password"
                  placeholder="Password"
                  value={coziPassword}
                  onChange={(e) => {
                    setCoziPassword(e.target.value);
                    setCoziTestSuccess(false);
                    setCoziTestError(null);
                    setCoziSaveSuccess(false);
                    setCoziSaveError(null);
                  }}
                  className="settings-input"
                  autoComplete="current-password"
                />
                {coziTestError && <p className="settings-error">{coziTestError}</p>}
                {coziTestSuccess && <p className="settings-success">Connection successful. You can save changes.</p>}
                {coziSaveError && <p className="settings-error">{coziSaveError}</p>}
                {coziSaveSuccess && <p className="settings-success">Credentials saved successfully.</p>}
                <div className="settings-actions">
                  <button
                    type="button"
                    className="settings-btn-secondary"
                    disabled={coziTestLoading || !coziUsername.trim() || !coziPassword}
                    onClick={async () => {
                      setCoziTestError(null);
                      setCoziTestSuccess(false);
                      setCoziTestLoading(true);
                      try {
                        const res = await fetch(`${apiBase}/api/TestCoziConnection`, {
                          method: "POST",
                          headers: { "Content-Type": "application/json" },
                          credentials: "include",
                          body: JSON.stringify({ username: coziUsername.trim(), password: coziPassword })
                        });
                        const data = await res.json().catch(() => ({}));
                        if (data.success) {
                          setCoziTestSuccess(true);
                          setCoziTestError(null);
                        } else {
                          setCoziTestError(data.message || "Connection failed.");
                          setCoziTestSuccess(false);
                        }
                      } catch (e) {
                        setCoziTestError(e.message || "Request failed.");
                        setCoziTestSuccess(false);
                      } finally {
                        setCoziTestLoading(false);
                      }
                    }}
                  >
                    {coziTestLoading ? withEllipsis("Testing") : "Test connection"}
                  </button>
                  <button
                    type="button"
                    className="settings-submit"
                    disabled={coziSaveLoading || !coziTestSuccess || !coziUsername.trim() || !coziPassword}
                    onClick={async () => {
                      setCoziSaveError(null);
                      setCoziSaveSuccess(false);
                      setCoziSaveLoading(true);
                      try {
                        const res = await fetch(`${apiBase}/api/UpdateCoziPassword`, {
                          method: "POST",
                          headers: { "Content-Type": "application/json" },
                          credentials: "include",
                          body: JSON.stringify({ username: coziUsername.trim(), password: coziPassword })
                        });
                        const data = await res.json().catch(() => ({}));
                        if (!res.ok) {
                          setCoziSaveError(data.message || `Error ${res.status}`);
                          return;
                        }
                        setCoziSaveSuccess(true);
                      } catch (e) {
                        setCoziSaveError(e.message || "Request failed.");
                      } finally {
                        setCoziSaveLoading(false);
                      }
                    }}
                  >
                    {coziSaveLoading ? withEllipsis("Saving") : "Save changes"}
                  </button>
                </div>
              </div>

              <div className="settings-section settings-section-paprika">
                <h3 className="settings-subheading">Paprika Cloud Sync</h3>
                <label htmlFor="paprika-email">Paprika email</label>
                <p className="settings-hint">
                  Same email and password you use for Paprika Recipe Manager cloud sync. Test connection, then save.
                  Stored in Azure Key Vault when the backend is configured.
                </p>
                <input
                  id="paprika-email"
                  type="email"
                  placeholder="you@example.com"
                  value={paprikaEmail}
                  onChange={(e) => {
                    setPaprikaEmail(e.target.value);
                    setPaprikaTestSuccess(false);
                    setPaprikaTestError(null);
                    setPaprikaSaveSuccess(false);
                    setPaprikaSaveError(null);
                  }}
                  className="settings-input"
                  autoComplete="username email"
                />
                <label htmlFor="paprika-password">Paprika password</label>
                <input
                  id="paprika-password"
                  type="password"
                  placeholder="Password"
                  value={paprikaPassword}
                  onChange={(e) => {
                    setPaprikaPassword(e.target.value);
                    setPaprikaTestSuccess(false);
                    setPaprikaTestError(null);
                    setPaprikaSaveSuccess(false);
                    setPaprikaSaveError(null);
                  }}
                  className="settings-input"
                  autoComplete="current-password"
                />
                {paprikaTestError && <p className="settings-error">{paprikaTestError}</p>}
                {paprikaTestSuccess && (
                  <p className="settings-success">Paprika connection OK. You can save credentials.</p>
                )}
                {paprikaSaveError && <p className="settings-error">{paprikaSaveError}</p>}
                {paprikaSaveSuccess && <p className="settings-success">Paprika credentials saved.</p>}
                <div className="settings-actions">
                  <button
                    type="button"
                    className="settings-btn-secondary"
                    disabled={paprikaTestLoading || !paprikaEmail.trim() || !paprikaPassword}
                    onClick={async () => {
                      setPaprikaTestError(null);
                      setPaprikaTestSuccess(false);
                      setPaprikaTestLoading(true);
                      try {
                        const res = await fetch(`${apiBase}/api/TestPaprikaConnection`, {
                          method: "POST",
                          headers: { "Content-Type": "application/json" },
                          credentials: "include",
                          body: JSON.stringify({
                            email: paprikaEmail.trim(),
                            password: paprikaPassword
                          })
                        });
                        const data = await res.json().catch(() => ({}));
                        if (data.success) {
                          setPaprikaTestSuccess(true);
                          setPaprikaTestError(null);
                        } else {
                          setPaprikaTestError(data.message || "Connection failed.");
                          setPaprikaTestSuccess(false);
                        }
                      } catch (e) {
                        setPaprikaTestError(e.message || "Request failed.");
                        setPaprikaTestSuccess(false);
                      } finally {
                        setPaprikaTestLoading(false);
                      }
                    }}
                  >
                    {paprikaTestLoading ? withEllipsis("Testing") : "Test Paprika"}
                  </button>
                  <button
                    type="button"
                    className="settings-submit"
                    disabled={
                      paprikaSaveLoading ||
                      !paprikaTestSuccess ||
                      !paprikaEmail.trim() ||
                      !paprikaPassword
                    }
                    onClick={async () => {
                      setPaprikaSaveError(null);
                      setPaprikaSaveSuccess(false);
                      setPaprikaSaveLoading(true);
                      try {
                        const res = await fetch(`${apiBase}/api/UpdatePaprikaPassword`, {
                          method: "POST",
                          headers: { "Content-Type": "application/json" },
                          credentials: "include",
                          body: JSON.stringify({
                            email: paprikaEmail.trim(),
                            password: paprikaPassword
                          })
                        });
                        const data = await res.json().catch(() => ({}));
                        if (!res.ok) {
                          setPaprikaSaveError(data.message || `Error ${res.status}`);
                          return;
                        }
                        setPaprikaSaveSuccess(true);
                      } catch (e) {
                        setPaprikaSaveError(e.message || "Request failed.");
                      } finally {
                        setPaprikaSaveLoading(false);
                      }
                    }}
                  >
                    {paprikaSaveLoading ? withEllipsis("Saving") : "Save Paprika"}
                  </button>
                </div>
              </div>

              <div className="settings-section settings-section-gmail">
                <h3 className="settings-subheading">Email approvals (Gmail)</h3>
                {gmailStatus?.adminOnly ? (
                  <p className="settings-hint">Only admins can connect Gmail and manage the email allowlist.</p>
                ) : (
                <>
                <p className="settings-hint">
                  Connect a Gmail inbox for AI email approvals. Family members on the allowlist can email
                  events, todos, groceries, or recipes; you approve in the app or by replying to the email.
                  OAuth tokens are stored in Azure Key Vault. Allowed senders are stored in a JSON config file (cached in memory).
                  If mail stops arriving, use Reconnect Gmail — Google refresh tokens can expire (7 days if the OAuth app is still in Testing) or be revoked.
                </p>
                {gmailStatusLoading && <p className="settings-hint">Loading Gmail status…</p>}
                {gmailStatusError && <p className="settings-error">{gmailStatusError}</p>}
                {gmailSaveSuccess && (
                  <p className="settings-success">Gmail settings updated.</p>
                )}
                {gmailSaveError && <p className="settings-error">{gmailSaveError}</p>}
                {gmailStatus && !gmailStatus.keyVaultConfigured && (
                  <p className="settings-error">Key Vault is not configured. Set KEY_VAULT_NAME in application settings.</p>
                )}
                {gmailStatus && !gmailStatus.oauthAppReady && (
                  <p className="settings-error">
                    Gmail OAuth app is not configured. An operator must set GMAIL_CLIENT_ID and GMAIL_CLIENT_SECRET once per deployment.
                  </p>
                )}
                {gmailStatus?.connected ? (
                  gmailStatus.accessOk === false ? (
                    <p className="settings-error">
                      Saved Gmail account{gmailStatus.email ? ` (${gmailStatus.email})` : ""} but authentication is no longer valid
                      {gmailStatus.accessError ? `: ${gmailStatus.accessError}` : "."}
                      {" "}Click Reconnect Gmail to grant a new refresh token.
                    </p>
                  ) : (
                    <p className="settings-success">
                      Connected: {gmailStatus.email || "Gmail account"}
                      {gmailStatus.pushEnabled ? " · push notifications enabled" : " · push not configured (operator must set GMAIL_PUBSUB_TOPIC)"}
                    </p>
                  )
                ) : (
                  <p className="settings-hint">No Gmail inbox connected yet.</p>
                )}
                {gmailStatus?.connected && gmailStatus.watchActive === false && (
                  <p className="settings-error">
                    Gmail push watch has expired, so new mail will not be checked automatically. Use Reconnect Gmail or open Gmail testing and click Renew watch.
                  </p>
                )}
                <label htmlFor="gmail-allowed-senders">Allowed senders</label>
                <input
                  id="gmail-allowed-senders"
                  type="text"
                  placeholder="you@example.com, spouse@example.com"
                  value={gmailAllowedInput}
                  onChange={(e) => {
                    setGmailAllowedInput(e.target.value);
                    setGmailSaveSuccess(false);
                    setGmailSaveError(null);
                  }}
                  className="settings-input"
                  autoComplete="email"
                />
                <p className="settings-hint">Comma-separated emails that may send items for approval. Only these addresses are processed.</p>
                <div className="settings-actions">
                  {!gmailStatus?.connected ? (
                    <a
                      href={`${apiBase}/api/GmailOAuthStart`}
                      className="settings-submit settings-link-btn"
                    >
                      Connect Gmail
                    </a>
                  ) : (
                    <>
                      <a
                        href={`${apiBase}/api/GmailOAuthStart`}
                        className={`settings-link-btn ${gmailStatus.needsReauth || gmailStatus.accessOk === false ? "settings-submit" : "settings-btn-secondary"}`}
                      >
                        Reconnect Gmail
                      </a>
                      <button
                        type="button"
                        className="settings-btn-secondary"
                        disabled={gmailDisconnectLoading}
                        onClick={async () => {
                          setGmailDisconnectLoading(true);
                          setGmailSaveError(null);
                          setGmailSaveSuccess(false);
                          try {
                            const res = await fetch(`${apiBase}/api/UpdateGmailSettings`, {
                              method: "POST",
                              headers: { "Content-Type": "application/json" },
                              credentials: "include",
                              body: JSON.stringify({})
                            });
                            const data = await res.json().catch(() => ({}));
                            if (!res.ok) throw new Error(data.message || `Error ${res.status}`);
                            setGmailSaveSuccess(true);
                            await loadGmailStatus();
                          } catch (err) {
                            setGmailSaveError(err.message || "Disconnect failed.");
                          } finally {
                            setGmailDisconnectLoading(false);
                          }
                        }}
                      >
                        {gmailDisconnectLoading ? withEllipsis("Disconnecting") : "Disconnect Gmail"}
                      </button>
                    </>
                  )}
                  <button
                    type="button"
                    className="settings-submit"
                    disabled={gmailSaveLoading}
                    onClick={async () => {
                      setGmailSaveError(null);
                      setGmailSaveSuccess(false);
                      setGmailSaveLoading(true);
                      try {
                        const allowedSenders = gmailAllowedInput
                          .split(/[,;\s]+/)
                          .map((s) => s.trim())
                          .filter(Boolean);
                        const res = await fetch(`${apiBase}/api/UpdateEmailAllowedSenders`, {
                          method: "POST",
                          headers: { "Content-Type": "application/json" },
                          credentials: "include",
                          body: JSON.stringify({ allowedSenders })
                        });
                        const data = await res.json().catch(() => ({}));
                        if (!res.ok) throw new Error(data.message || `Error ${res.status}`);
                        setGmailSaveSuccess(true);
                        setGmailAllowedInput((data.allowedSenders || allowedSenders).join(", "));
                        await loadGmailStatus();
                      } catch (err) {
                        setGmailSaveError(err.message || "Save failed.");
                      } finally {
                        setGmailSaveLoading(false);
                      }
                    }}
                  >
                    {gmailSaveLoading ? withEllipsis("Saving") : "Save allowlist"}
                  </button>
                </div>
                </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <div
        className="calendar-body"
        style={calendarFont ? { fontFamily: `"${calendarFont}", sans-serif` } : undefined}
      >
      {/* Daily agenda modal (from +N more in month view) */}
      {dailyAgendaDay && (
        <div className="daily-agenda-overlay" onClick={() => setDailyAgendaDay(null)}>
          <div className="daily-agenda-modal" onClick={(e) => e.stopPropagation()}>
            <div className="daily-agenda-header">
              <h2 className="daily-agenda-title">{format(dailyAgendaDay, "EEEE, MMMM d, yyyy")}</h2>
              <button type="button" className="daily-agenda-close" onClick={() => setDailyAgendaDay(null)} aria-label="Close">{SYM.close}</button>
            </div>
            <div className="daily-agenda-list">
              {eventsForDay(dailyAgendaDay).map((event, index) => (
                <React.Fragment key={event.id}>
                  <div
                    className="daily-agenda-event"
                    style={{ background: eventBackground(event.participantColors) }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedEvent(event);
                      setDailyAgendaDay(null);
                    }}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setSelectedEvent(event);
                        setDailyAgendaDay(null);
                      }
                    }}
                  >
                    <div className="event-title">{event.title}</div>
                    <EventLocation location={event.location} />
                    {event.allDay && formatMultiDayRange(event) && (
                      <div className="event-time">{formatMultiDayRange(event)}</div>
                    )}
                    {!event.allDay && <div className="event-time">{formatTimeRange(event)}</div>}
                  </div>
                  {needsDivider(eventsForDay(dailyAgendaDay), index) && <div className="event-divider" />}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* View navigation */}
      <div className="view-controls">
        <div className="view-nav">
          <button type="button" className="nav-btn" onClick={goPrev} aria-label="Previous">
            {SYM.chevronLeft}
          </button>
          <div ref={viewTitleRef} className="view-title-wrap">
            <button
              type="button"
              className="view-title-btn"
              onClick={() => {
                if (viewMode === "week") {
                  setPickerMonth(startOfMonth(viewAnchor));
                  setWeekPickerOpen((o) => !o);
                  setMonthPickerOpen(false);
                } else {
                  setPickerYear(viewAnchor.getFullYear());
                  setMonthPickerOpen((o) => !o);
                  setWeekPickerOpen(false);
                }
              }}
              aria-expanded={weekPickerOpen || monthPickerOpen}
              aria-haspopup="true"
            >
              {viewTitle}
            </button>
            {weekPickerOpen && viewMode === "week" && (
              <div className="week-picker-popover">
                <div className="week-picker-header">
                  <button type="button" className="week-picker-today" onClick={() => { goToToday(); setWeekPickerOpen(false); }}>
                    Today
                  </button>
                  <div className="week-picker-month-nav">
                    <button type="button" className="week-picker-nav" onClick={() => setPickerMonth((m) => addMonths(m, -1))} aria-label="Previous month">{SYM.chevronLeft}</button>
                    <span className="week-picker-month-label">{format(pickerMonth, "MMMM yyyy")}</span>
                    <button type="button" className="week-picker-nav" onClick={() => setPickerMonth((m) => addMonths(m, 1))} aria-label="Next month">{SYM.chevronRight}</button>
                  </div>
                </div>
                <div className="week-picker-calendar">
                  <div className="week-picker-weekdays">
                    {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
                      <span key={d} className="week-picker-weekday">{d}</span>
                    ))}
                  </div>
                  {getPickerCalendarGrid(pickerMonth).map((week, wi) => (
                    <div key={wi} className="week-picker-row">
                      {week.map((day) => {
                        const inMonth = isSameMonth(day, pickerMonth);
                        const isTodayDate = isToday(day);
                        return (
                          <button
                            key={day.toISOString()}
                            type="button"
                            className={`week-picker-day ${!inMonth ? "other-month" : ""} ${isTodayDate ? "today" : ""}`}
                            onClick={() => {
                              setViewAnchor(startOfWeek(day, { weekStartsOn: 0 }));
                              setViewMode("week");
                              setWeekPickerOpen(false);
                            }}
                          >
                            {format(day, "d")}
                          </button>
                        );
                      })}
                    </div>
                  ))}
                </div>
              </div>
            )}
            {monthPickerOpen && viewMode === "month" && (
              <div className="month-picker-dropdown">
                <button type="button" className="month-picker-today" onClick={() => { goToToday(); setMonthPickerOpen(false); }}>
                  Today
                </button>
                <div className="month-picker-year-row">
                  <button type="button" className="month-picker-year-nav" onClick={() => setPickerYear((y) => y - 1)} aria-label="Previous year">{SYM.chevronLeft}</button>
                  <span className="month-picker-year-label">{pickerYear}</span>
                  <button type="button" className="month-picker-year-nav" onClick={() => setPickerYear((y) => y + 1)} aria-label="Next year">{SYM.chevronRight}</button>
                </div>
                <div className="month-picker-months">
                  {MONTH_NAMES.map((name, i) => (
                    <button
                      key={name}
                      type="button"
                      className="month-picker-month-btn"
                      onClick={() => {
                        const d = startOfMonth(new Date(pickerYear, i, 1));
                        setViewAnchor(d);
                        setViewMode("month");
                        setMonthPickerOpen(false);
                      }}
                    >
                      {name}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
          <button type="button" className="nav-btn" onClick={goNext} aria-label="Next">
            {SYM.chevronRight}
          </button>
        </div>
      </div>
      
      {/* Legend */}
      {legendItems.length > 0 && (
        <div className="legend">
          {legendItems.map(item => (
            <span
              key={item.name}
              className="legend-item"
              style={{
                borderColor: item.color,
                backgroundColor: item.color,
                color: textColorForBg(item.color)
              }}
            >
              {item.name}
            </span>
          ))}
        </div>
      )}

      {loading && <p className="loading">{withEllipsis("Loading events")}</p>}
      {loadError && !hasLoaded && <p className="error">Could not load events: {loadError}</p>}
      {actionError && <p className="error">Could not save changes: {actionError}</p>}

      <div
        className="view-swipe-area"
        onTouchStart={handleSwipeStart}
        onTouchEnd={handleSwipeEnd}
      >
      {viewMode === "month" ? (
        /* Month view: calendar grid */
        <div className="month-view">
          <div className="month-view-scroll">
            <div className="month-grid-inner">
          <div className="month-grid-header">
            {MONTH_WEEKDAY_LABELS.map((d) => (
              <div key={d.full} className="month-grid-header-cell">
                <span className="month-dow-full">{d.full}</span>
                <span className="month-dow-short" aria-hidden="true">{d.short}</span>
              </div>
            ))}
          </div>
          {monthWeeks.map((week, wi) => (
            <div key={wi} className="month-grid-row">
              {week.map((day) => {
                const dayEvents = eventsForDay(day);
                const dayStr = format(day, "yyyy-MM-dd");
                const dayWeather = weather[dayStr];
                const weatherDisplay = formatWeatherDisplay(dayWeather);
                const inMonth = isSameMonth(day, viewAnchor);
                const today = isToday(day);
                return (
                  <div
                    key={dayStr}
                    ref={today ? todayColumnRef : null}
                    className={`month-cell ${!inMonth ? "other-month" : ""} ${today ? "today" : ""} ${dropTargetDay === dayStr ? "drop-target" : ""}`}
                    onClick={() => handleDayClick(day)}
                    role="button"
                    tabIndex={0}
                    onKeyPress={(e) => {
                      if (e.key === "Enter" || e.key === " ") handleDayClick(day);
                    }}
                    onDragOver={(e) => handleDragOver(e, day)}
                    onDragLeave={handleDragLeave}
                    onDrop={(e) => handleDrop(e, day)}
                  >
                    <div className="month-cell-header">
                      <span className="month-cell-date">{format(day, "d")}</span>
                      {weatherDisplay && <span className="month-cell-weather">{weatherDisplay}</span>}
                    </div>
                    <div className="month-cell-events">
                      {dayEvents.slice(0, 6).map((event) => {
                        const shortTime = formatShortTime(event);
                        const lineText = shortTime ? `${shortTime} ${event.title}` : event.title;
                        return (
                          <div
                            key={event.id}
                            className={`event month-event ${draggedEventId === event.id ? "dragging" : ""}`}
                            style={{ background: eventBackground(event.participantColors) }}
                            draggable
                            onDragStart={(e) => handleDragStart(e, event)}
                            onDragEnd={handleDragEnd}
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedEvent(event);
                            }}
                            title={event.title}
                          >
                            <span className="month-event-line">{lineText}</span>
                          </div>
                        );
                      })}
                      {dayEvents.length > 6 && (
                        <div
                          className="month-more"
                          role="button"
                          tabIndex={0}
                          onClick={(e) => {
                            e.stopPropagation();
                            setDailyAgendaDay(day);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              e.stopPropagation();
                              setDailyAgendaDay(day);
                            }
                          }}
                          aria-label={`View all ${dayEvents.length} events`}
                        >
                          +{dayEvents.length - 6} more
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
            </div>
          </div>
        </div>
      ) : (
        <>
      {/* First Row: Sun - Wed */}
      <div className="week-grid">
        {firstRow.map(day => {
          const dayEvents = eventsForDay(day);
          const dayStr = format(day, "yyyy-MM-dd");
          const dayWeather = weather[dayStr];
          const weatherDisplay = formatWeatherDisplay(dayWeather);
          const isToday = dayStr === format(new Date(), "yyyy-MM-dd");
          
          return (
            <div
              key={day}
              ref={isToday ? todayColumnRef : null}
              className={`day-column ${isToday ? "today" : ""} ${dropTargetDay === format(day, "yyyy-MM-dd") ? "drop-target" : ""}`}
              onClick={() => handleDayClick(day)}
              role="button"
              tabIndex={0}
              onKeyPress={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  handleDayClick(day);
                }
              }}
              onDragOver={(e) => handleDragOver(e, day)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, day)}
            >
              <div className="day-header">
                {format(day, "EEE M/d")}
                {weatherDisplay && <span className="day-weather">{weatherDisplay}</span>}
              </div>
              <div className="day-events">
                {dayEvents.map((event, index) => (
                  <React.Fragment key={event.id}>
                    <div
                      className={`event ${draggedEventId === event.id ? "dragging" : ""}`}
                      style={{
                        background: eventBackground(event.participantColors)
                      }}
                      draggable
                      onDragStart={(e) => handleDragStart(e, event)}
                      onDragEnd={handleDragEnd}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedEvent(event);
                      }}
                    >
                      <div className="event-title">{event.title}</div>
                      <EventLocation location={event.location} />
                      {event.allDay && formatMultiDayRange(event) && (
                        <div className="event-time">{formatMultiDayRange(event)}</div>
                      )}
                      {!event.allDay && <div className="event-time">{formatTimeRange(event)}</div>}
                    </div>
                    {needsDivider(dayEvents, index) && <div className="event-divider"></div>}
                  </React.Fragment>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Second Row: Thu - Sun */}
      <div className="week-grid">
        {secondRow.map(day => {
          const dayEvents = eventsForDay(day);
          const dayStr = format(day, "yyyy-MM-dd");
          const dayWeather = weather[dayStr];
          const weatherDisplay = formatWeatherDisplay(dayWeather);
          const isToday = dayStr === format(new Date(), "yyyy-MM-dd");
          
          return (
            <div
              key={day}
              ref={isToday ? todayColumnRef : null}
              className={`day-column ${isToday ? "today" : ""} ${dropTargetDay === format(day, "yyyy-MM-dd") ? "drop-target" : ""}`}
              onClick={() => handleDayClick(day)}
              role="button"
              tabIndex={0}
              onKeyPress={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  handleDayClick(day);
                }
              }}
              onDragOver={(e) => handleDragOver(e, day)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, day)}
            >
              <div className="day-header">
                {format(day, "EEE M/d")}
                {weatherDisplay && <span className="day-weather">{weatherDisplay}</span>}
              </div>
              <div className="day-events">
                {dayEvents.map((event, index) => (
                  <React.Fragment key={event.id}>
                    <div
                      className={`event ${draggedEventId === event.id ? "dragging" : ""}`}
                      style={{
                        background: eventBackground(event.participantColors)
                      }}
                      draggable
                      onDragStart={(e) => handleDragStart(e, event)}
                      onDragEnd={handleDragEnd}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedEvent(event);
                      }}
                    >
                      <div className="event-title">{event.title}</div>
                      <EventLocation location={event.location} />
                      {event.allDay && formatMultiDayRange(event) && (
                        <div className="event-time">{formatMultiDayRange(event)}</div>
                      )}
                      {!event.allDay && <div className="event-time">{formatTimeRange(event)}</div>}
                    </div>
                    {needsDivider(dayEvents, index) && <div className="event-divider"></div>}
                  </React.Fragment>
                ))}
              </div>
            </div>
          );
        })}
      </div>
        </>
      )}
      </div>
      </div>

{/* Assistant FAB — own control, not inside Smart Add */}
{!settingsOpen && !selectedEvent && !selectedDate && !smartAddMode && !assistantOpen && activeTab !== "games" && (
  <AssistantFab onClick={() => setAssistantOpen(true)} />
)}

{/* Smart Add FAB + Modal */}
{!settingsOpen && !selectedEvent && !selectedDate && !smartAddMode && (
  <SmartAddFAB onSelect={(mode) => {
    if (mode === "manual") {
      setSelectedDate(new Date());
      setSelectedEvent(null);
      setInitialFormData(null);
    } else {
      setSmartAddMode(mode);
    }
  }} />
)}
{smartAddMode && (
  <SmartAddModal
    mode={smartAddMode}
    participants={
      familyMembers.length > 0
        ? familyMembers.map((m) => m.name).sort((a, b) => a.localeCompare(b))
        : getLegendItems().map((item) => item.name)
    }
    onCreateEvent={async (eventData, { deferRefresh = false } = {}) => {
      const result = await createEvent(eventData);
      if (!deferRefresh) await fetchEvents();
      return result;
    }}
    onClose={() => {
      setSmartAddMode(null);
      fetchEvents();
    }}
    apiBase={apiBase}
    promptContext={aiPromptContext}
  />
)}

{/* Event Edit/Add Modal */}
{(selectedEvent || selectedDate) && (
  <EventModal
    event={selectedEvent}
    selectedDate={selectedDate}
    initialFormData={initialFormData}
    onClose={() => {
      setSelectedEvent(null);
      setSelectedDate(null);
      setInitialFormData(null);
      setPendingApprovalRequest(null);
    }}
    onSave={async (updatedEvent) => {
      try {
        setActionError(null);
        if (selectedEvent && isRecurringInstance(updatedEvent)) {
          const scopeLabel = updatedEvent.editScope === "series" ? "the entire series" : "this occurrence only";
          if (!window.confirm(`Save changes to ${scopeLabel}?`)) return;
        }
        setIsSaving(true);
        if (pendingApprovalRequest) {
          const items = (pendingApprovalRequest.items || []).map((it, idx) => ({
            itemId: it.itemId,
            summary: updatedEvent.title || it.summary,
            payload: idx === 0 ? {
              title: updatedEvent.title,
              startDate: updatedEvent.startDate,
              endDate: updatedEvent.endDate,
              startTime: updatedEvent.startTime,
              endTime: updatedEvent.endTime,
              allDay: updatedEvent.allDay,
              location: updatedEvent.location,
              notes: updatedEvent.notes,
              participants: updatedEvent.participants || updatedEvent.selectedParticipants || [],
              recurrence: updatedEvent.recurrence,
              reminders: updatedEvent.reminders,
            } : (it.payload || it),
          }));
          const res = await fetch(`${apiBase}/api/ApproveApprovalRequest`, {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              requestId: pendingApprovalRequest.requestId,
              items,
              partial: !!pendingApprovalRequest.partial
            }),
          });
          if (!res.ok) throw new Error(await res.text());
          await fetchEvents();
          setPendingApprovalRequest(null);
          refreshApprovalBell();
          if (pendingApprovalRequest.partial) {
            setApprovalsPanelOpen(true);
          }
        } else if (selectedEvent) {
          if (isRecurringInstance(updatedEvent)) {
            if (updatedEvent.editScope === "series") {
              await updateEvent(updatedEvent);
              await fetchEvents();
            } else {
              await deleteEvent({ ...updatedEvent, extent: "day" });
              await createEvent(updatedEvent);
              await fetchEvents();
            }
          } else {
            await updateEvent(updatedEvent);
            await fetchEvents();
          }
        } else {
          await createEvent(updatedEvent);
          await fetchEvents();
        }
        setSelectedEvent(null);
        setSelectedDate(null);
        setInitialFormData(null);
      } catch (err) {
        console.error("Error saving event:", err);
        setActionError(err.message);
      } finally {
        setIsSaving(false);
      }
    }}
    onDelete={async (eventToDelete) => {
      try {
        setActionError(null);
        setIsDeleting(true);
        // Recurring: extent is set by EventModal delete confirmation (day | forward)
        const extent = eventToDelete.extent != null ? eventToDelete.extent : "day";
        if (!isRecurringInstance(eventToDelete)) {
          const confirmed = window.confirm("Are you sure you want to delete this event?");
          if (!confirmed) {
            setIsDeleting(false);
            return;
          }
        }
        await deleteEvent({ ...eventToDelete, extent });
        await fetchEvents();
        setSelectedEvent(null);
        setSelectedDate(null);
      } catch (err) {
        console.error("Error deleting event:", err);
        setActionError(err.message);
      } finally {
        setIsDeleting(false);
      }
    }}
    onCopy={(copiedData) => {
      setSelectedEvent(null);
      setSelectedDate(parseISO(copiedData.startDate + "T12:00:00"));
      setInitialFormData({
        title: copiedData.title || "",
        location: copiedData.location || "",
        notes: copiedData.notes || "",
        selectedParticipants: copiedData.selectedParticipants || copiedData.participants || [],
        allDay: !!copiedData.allDay,
        startDate: copiedData.startDate,
        startTime: copiedData.startTime || "09:00",
        endDate: copiedData.endDate,
        endTime: copiedData.endTime || "10:00",
      });
    }}
    participants={
      familyMembers.length > 0
        ? familyMembers.map((m) => m.name).sort((a, b) => a.localeCompare(b))
        : getLegendItems().map((item) => item.name)
    }
    isSaving={isSaving}
    isDeleting={isDeleting}
  />
)}
    </div>

      {activeTab === "groceries" && <GroceriesPage apiBase={apiBase} />}
      {activeTab === "meals" && <MealsPage apiBase={apiBase} />}
      {activeTab === "todo" && (
        <TodoPage
          apiBase={apiBase}
          familyMembers={familyMembers}
          userEmail={userEmail}
          memberId={resolvedTodoMemberId}
          onChangeMemberId={handleChangeTodoMember}
        />
      )}
      {activeTab === "games" && (
        <GamesPage
          apiBase={apiBase}
          familyMembers={familyMembers}
          onInGameChange={setGamesInGame}
        />
      )}

      {!(activeTab === "games" && gamesInGame) && (
        <nav className="bottom-nav" aria-label="Bottom navigation">
          <button
            type="button"
            className={`bottom-nav-btn ${activeTab === "calendars" ? "active" : ""}`}
            onClick={() => switchTab("calendars")}
          >
            Calendars
          </button>
          <button
            type="button"
            className={`bottom-nav-btn ${activeTab === "groceries" ? "active" : ""}`}
            onClick={() => switchTab("groceries")}
          >
            Groceries
          </button>
          <button
            type="button"
            className={`bottom-nav-btn ${activeTab === "meals" ? "active" : ""}`}
            onClick={() => switchTab("meals")}
          >
            Meals
          </button>
          <button
            type="button"
            className={`bottom-nav-btn ${activeTab === "todo" ? "active" : ""}`}
            onClick={() => switchTab("todo")}
          >
            To-Do
          </button>
          <button
            type="button"
            className={`bottom-nav-btn ${activeTab === "games" ? "active" : ""}`}
            onClick={() => switchTab("games")}
          >
            Games
          </button>
        </nav>
      )}
    </div>
  );
}
